import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

export type LessonType = 'video' | 'pdf';

@Component({
  selector: 'app-lesson-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './lesson-form.html',
})
export class LessonForm {
  @Output() save = new EventEmitter<{
    title: string;
    description: string;
    type: LessonType;
    duration: number;
    videoUrl?: string;
    pdfUrl?: string;
  }>();
  @Output() canceled = new EventEmitter<void>();

  private readonly fb = inject(FormBuilder);
  protected lessonForm: FormGroup;

  protected lessonTypes: Array<{
    value: LessonType;
    label: string;
    icon: string;
  }> = [
    { value: 'video', label: 'Vídeo', icon: 'bi-play-circle' },
    { value: 'pdf', label: 'PDF', icon: 'bi-file-pdf' },
  ];

  constructor() {
    this.lessonForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      type: ['video', Validators.required],
      duration: [0, [Validators.required, Validators.min(1)]],
      videoUrl: [''],
      pdfUrl: [''],
    });

    // Adicionar validação condicional baseada no tipo
    this.lessonForm.get('type')?.valueChanges.subscribe((type) => {
      const videoUrlControl = this.lessonForm.get('videoUrl');
      const pdfUrlControl = this.lessonForm.get('pdfUrl');

      if (type === 'video') {
        videoUrlControl?.setValidators([Validators.required]);
        pdfUrlControl?.clearValidators();
        pdfUrlControl?.setValue(''); // Limpar campo PDF quando selecionar vídeo
      } else if (type === 'pdf') {
        pdfUrlControl?.setValidators([Validators.required]);
        videoUrlControl?.clearValidators();
        videoUrlControl?.setValue(''); // Limpar campo vídeo quando selecionar PDF
      } else {
        videoUrlControl?.clearValidators();
        pdfUrlControl?.clearValidators();
        videoUrlControl?.setValue(''); // Limpar campos quando selecionar outro tipo
        pdfUrlControl?.setValue('');
      }

      videoUrlControl?.updateValueAndValidity({ emitEvent: false });
      pdfUrlControl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  protected onSave(): void {
    if (this.lessonForm.invalid) {
      this.lessonForm.markAllAsTouched();
      return;
    }

    const formValue = this.lessonForm.value;
    this.save.emit({
      title: formValue.title,
      description: formValue.description,
      type: formValue.type,
      duration: formValue.duration,
      videoUrl: formValue.videoUrl || undefined,
      pdfUrl: formValue.pdfUrl || undefined,
    });
  }

  protected onCancel(): void {
    this.canceled.emit();
  }

  protected getFieldError(fieldName: string): string {
    const field = this.lessonForm.get(fieldName);
    if (!field || !field.errors || !field.touched) {
      return '';
    }

    if (field.errors['required']) {
      return 'Este campo é obrigatório';
    }
    if (field.errors['minlength']) {
      const requiredLength = field.errors['minlength'].requiredLength;
      return `Deve ter pelo menos ${requiredLength} caracteres`;
    }
    if (field.errors['min']) {
      return 'Duração deve ser maior que 0';
    }
    if (field.errors['url']) {
      return 'URL inválida';
    }

    return '';
  }

  protected isFieldInvalid(fieldName: string): boolean {
    const field = this.lessonForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }
}
